import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonList,
  IonCard,
  IonCardContent,
  useIonRouter,
  useIonAlert,
  IonText,
  IonSpinner,
  IonLoading
} from '@ionic/react';
import { close, time as timeIcon, document } from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import './TimeRegistrationV2Register.css';
import getApi from '../api';
import { useLocalStorage } from '../components/useLocalStorage';

interface TimeEntry {
  date: string;
  startTime: string;
  endTime: string;
  workedHours: number;
  description: string;
  projectId: string;
  projectName: string;
  customerName?: string;
  customerId?: string;
  billableHours?: number;
  activity?: string;
  comment?: string;
  travelHours?: number;
  traktamente?: string; // "", "Halv", "Hel"
}

const TimeRegistrationV2Register: React.FC = () => {
  const { projectId, registrationId } = useParams<{ projectId?: string; registrationId?: string }>();
  const router = useIonRouter();
  const [presentAlert] = useIonAlert();
  const [me] = useLocalStorage("me", "");
  const api = getApi();

  const initialDate = localStorage.getItem('trv_selectedDate') || new Date().toISOString().split('T')[0];
  const initialCustomerName = localStorage.getItem('trv_customerName') || '';
  const initialCustomerId = localStorage.getItem('trv_customerId') || '';
  const initialInternalTimeName = localStorage.getItem('trv_internalTimeName') || '';
  
  // Kontrollera om det är intern tid
  const isInternalTime = projectId === 'internal';
  
  const [entry, setEntry] = useState<TimeEntry>({
    date: initialDate,
    startTime: '08:00',
    endTime: '17:00',
    workedHours: 0,
    description: '',
    projectId: isInternalTime ? '' : (projectId || ''),
    projectName: '',
    customerName: isInternalTime ? initialInternalTimeName : initialCustomerName,
    customerId: initialCustomerId,
    billableHours: 0,
    activity: 'Projektledning',
    comment: '',
    travelHours: 0,
    traktamente: ''
  });

  const [breaks, setBreaks] = useState<number>(0);

  const [projectName, setProjectName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (registrationId) {
      // Redigeringsläge - ladda befintlig registrering
      setIsEditMode(true);
      loadExistingRegistration(registrationId);
    } else if (projectId && projectId !== 'internal') {
      // Nytt registreringsläge med order
      setIsEditMode(false);
      loadProjectName();
      calculateHours();
    } else if (isInternalTime) {
      // Nytt registreringsläge med intern tid
      setIsEditMode(false);
      setProjectName(initialInternalTimeName);
      calculateHours();
    }

    // Cleanup: Ta bort customer/internal info från localStorage när komponenten unmountas
    return () => {
      localStorage.removeItem('trv_customerName');
      localStorage.removeItem('trv_customerId');
      localStorage.removeItem('trv_internalTimeName');
    };
  }, [projectId, registrationId]);

  useEffect(() => {
    calculateHours();
  }, [entry.startTime, entry.endTime]);

  const loadExistingRegistration = async (id: string) => {
    try {
      const response = await api.get(`/user/timeregister/${id}/`);
      if (response.data) {
        const reg = response.data;
        setEntry({
          date: reg.date,
          startTime: reg.check_in,
          endTime: reg.check_out || '',
          workedHours: parseFloat(reg.hours) || 0,
          description: reg.desc || '',
          projectId: reg.order_id || '',
          projectName: `Order ${reg.order_id}`,
          customerName: reg.customer_name || '',
          customerId: reg.customer_id || '',
          billableHours: parseFloat(reg.hours) || 0,
          activity: 'Projektledning',
          comment: reg.desc || '',
          travelHours: 0,
          traktamente: reg.traktamente || ''
        });
        setBreaks(reg.breaks || 0);
        setProjectName(reg.customer_name || `Order ${reg.order_id}`);
      }
    } catch (error) {
      console.error('Failed to load registration:', error);
      presentAlert({
        header: 'Fel',
        message: 'Det gick inte att ladda tidsregistreringen.',
        buttons: ['OK']
      });
    }
  };

  const loadProjectName = async () => {
    // Använd kundnamn från localStorage om tillgängligt
    const customerName = localStorage.getItem('trv_customerName');
    const name = customerName || `Projekt ${projectId}`;
    setProjectName(name);
    setEntry(prev => ({ ...prev, projectName: name }));
  };

  const calculateHours = () => {
    if (entry.startTime && entry.endTime) {
      const start = new Date(`2000-01-01T${entry.startTime}`);
      const end = new Date(`2000-01-01T${entry.endTime}`);
      
      if (end > start) {
        const diffMs = end.getTime() - start.getTime();
        const hours = diffMs / (1000 * 60 * 60);
        const roundedHours = Math.round(hours * 4) / 4; // Round to nearest 0.25
        
        setEntry(prev => ({
          ...prev,
          workedHours: roundedHours,
          billableHours: roundedHours
        }));
      }
    }
  };

  const handleSave = async () => {
    // Validering: Kommentar är obligatorisk för intern tid
    if (!entry.projectId && !entry.comment) {
      presentAlert({
        header: 'Fel',
        message: 'Intern kommentar är obligatorisk',
        buttons: ['OK']
      });
      return;
    }

    // Beräkna total arbetad tid i timmar
    const [startHour, startMin] = entry.startTime.split(':').map(Number);
    const [endHour, endMin] = entry.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    // Hantera om slutet är nästa dag (t.ex. 17:00 -> 02:00)
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }
    
    const totalMinutes = endMinutes - startMinutes;
    const totalHours = totalMinutes / 60;
    
    // Kontrollera om över 4 timmar utan rast
    if (totalHours > 4 && breaks === 0) {
      return new Promise<void>((resolve) => {
        presentAlert({
          header: 'Ingen rast registrerad',
          message: 'Du har arbetat mer än 4 timmar utan rast. Vill du fortsätta utan rast eller lägga till en rast?',
          buttons: [
            {
              text: 'Lägg till rast',
              role: 'cancel',
              handler: () => {
                resolve();
                // Fokusera på rast-fältet eller scrolla dit
                // Användaren kan nu redigera rast innan de sparar igen
              }
            },
            {
              text: 'Jag har inte haft någon rast',
              handler: () => {
                // Fortsätt med sparande
                performSave();
                resolve();
              }
            }
          ]
        });
      });
    }

    // Om validering OK, spara direkt
    await performSave();
  };

  const performSave = async () => {
    setIsSaving(true);

    // För intern tid: kombinera customerName (intern tid-namn) med kommentar
    let description = entry.comment || entry.description;
    if (!entry.projectId && entry.customerName && entry.comment) {
      // Intern tid: "Drift - Min kommentar"
      description = `${entry.customerName} - ${entry.comment}`;
    }

    const timeData: any = {
      date: entry.date,
      check_in: entry.startTime,
      check_out: entry.endTime,
      desc: description,
      hours: entry.workedHours.toString(),
      order_id: entry.projectId || '',
      user: me.id,
      edited: "True",
      breaks: breaks
    };
    
    // För intern tid, sätt customer_name
    if (!entry.projectId && entry.customerName) {
      timeData.customer_name = entry.customerName;
    } else if (entry.customerName) {
      // För order, sätt customer_name från sökning
      timeData.customer_name = entry.customerName;
    }
    
    if (entry.customerId) {
      timeData.customer_id = entry.customerId;
    }
    
    // Skicka alltid med traktamente (även om det är tomt för att kunna ta bort befintligt)
    timeData.traktamente = entry.traktamente || '';

    try {
      let response;
      if (isEditMode && registrationId) {
        // Uppdatera befintlig registrering
        response = await api.patch(`/user/timeregister/${registrationId}/`, timeData);
      } else {
        // Skapa ny registrering
        response = await api.post('/user/timeregister/', timeData);
      }
      
      if (response.status === 200 || response.status === 201) {
        // Trigga en reload genom att dispatcha ett event
        window.dispatchEvent(new Event('timeRegistrationUpdated'));
        // Navigera tillbaka
        router.push('/TimeRegistrationV2', 'back');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsSaving(false);
      presentAlert({
        header: 'Fel',
        message: 'Det gick inte att spara tiden.',
        buttons: ['OK']
      });
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !registrationId) return;

    presentAlert({
      header: 'Ta bort tidsregistrering',
      message: 'Är du säker på att du vill ta bort denna tidsregistrering?',
      buttons: [
        { text: 'Avbryt', role: 'cancel' },
        { 
          text: 'Ta bort', 
          role: 'destructive',
          handler: async () => {
            try {
              await api.delete(`/user/timeregister/${registrationId}/`);
              // Trigga en reload genom att dispatcha ett event
              window.dispatchEvent(new Event('timeRegistrationUpdated'));
              router.push('/TimeRegistrationV2', 'back');
            } catch (error) {
              console.error('Failed to delete registration:', error);
              presentAlert({
                header: 'Fel',
                message: 'Det gick inte att ta bort registreringen.',
                buttons: ['OK']
              });
            }
          }
        }
      ]
    });
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonButton onClick={() => router.push('/TimeRegistrationV2', 'back')} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonIcon icon={close} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </IonButton>
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {isEditMode ? 'Redigera tidsregistrering' : 'Ny tidsregistrering'}
          </IonTitle>
          <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonButton onClick={handleSave} strong={true} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              Spara
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="time-registration-register" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <div className="form-container">
          {/* Projekt/Intern tid info */}
          <IonCard className="info-card" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <div className="project-header">
                <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <h2 className="project-title">
                    {isInternalTime ? projectName : `${projectId} - ${projectName}`}
                  </h2>
                  {isInternalTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>Studio 27 AB</p>
                  )}
                </IonText>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Datum */}
          <IonList className="form-list" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon icon={timeIcon} className="field-icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                Datum
              </IonLabel>
              <IonInput
                type="date"
                value={entry.date}
                onIonChange={e => setEntry({ ...entry, date: e.detail.value! })} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}              />
            </IonItem>

            {/* Klockning */}
            <div className="time-section">
              <div className="time-section-header">
                <IonIcon icon={timeIcon} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                <span>Klockning</span>
              </div>
              
              <div className="time-inputs">
                <IonItem className="time-item" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Från</IonLabel>
                  <IonInput
                    type="time"
                    value={entry.startTime}
                    onIonChange={e => setEntry({ ...entry, startTime: e.detail.value! })} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                  />
                </IonItem>
                
                <IonItem className="time-item" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Till</IonLabel>
                  <IonInput
                    type="time"
                    value={entry.endTime}
                    onIonChange={e => setEntry({ ...entry, endTime: e.detail.value! })} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                  />
                </IonItem>
              </div>
            </div>

            {/* Arbetade timmar */}
            <div className="hours-section">
              <div className="hours-section-header">
                <IonIcon icon={timeIcon} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                <span>Arbetade timmar</span>
              </div>
              
              <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Arbetade timmar</IonLabel>
                <IonInput
                  type="number"
                  value={entry.workedHours}
                  readonly
                  className="hours-display" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                />
              </IonItem>

              <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Rast (minuter)</IonLabel>
                <IonInput
                  type="number"
                  value={breaks}
                  onIonChange={e => setBreaks(parseInt(e.detail.value!) || 0)}
                  placeholder="0"
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </IonItem>
            </div>

            {/* Tillägg / Traktamente */}
            <div className="additional-section">
              <div className="section-header">Tillägg</div>
              <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Traktamente</IonLabel>
                <IonSelect
                  value={entry.traktamente}
                  placeholder="Välj traktamente"
                  interface="action-sheet"
                  onIonChange={e => setEntry(prev => ({ ...prev, traktamente: e.detail.value! }))}
                  onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                >
                  <IonSelectOption value="" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inget</IonSelectOption>
                  <IonSelectOption value="Halv" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Halv</IonSelectOption>
                  <IonSelectOption value="Hel" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Hel</IonSelectOption>
                </IonSelect>
              </IonItem>
            </div>

            {/* Kommentar */}
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Kommentar</IonLabel>
              <IonTextarea
                value={entry.comment}
                onIonChange={e => setEntry({ ...entry, comment: e.detail.value! })}
                placeholder="Projektmöte hos kund"
                rows={3} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}              />
            </IonItem>

            {/* Resa */}
            {/* <div className="travel-section">
              <IonItem button detail placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <h2>Resa</h2>
                  <p>{entry.travelHours || 0} h</p>
                </IonLabel>
              </IonItem>
            </div> */}
          </IonList>

          {/* Ta bort knapp - visa bara i redigeringsläge */}
          {isEditMode && (
            <div className="delete-button-container">
              <IonButton
                expand="block"
                color="danger"
                fill="clear"
                onClick={handleDelete} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}              >
                Ta bort tidsregistrering
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>

      {/* Loading spinner */}
      <IonLoading
        isOpen={isSaving}
        message={isEditMode ? 'Sparar ändringar...' : 'Skapar tidsregistrering...'}
      />
    </IonPage>
  );
};

export default TimeRegistrationV2Register;
